import { createContext, useContext, useRef } from "react";

import PropTypes from "prop-types";

const VideoEventBusContext = createContext();

const propTypes = {
  children: PropTypes.object,
};

export const VideoEventBusContextProvider = ({ children }) => {
  const videoEventBus = useRef();
  const unmountedEvents = useRef([]);

  const mountOutstandingListeners = () => {
    unmountedEvents.current?.forEach((listener) => {
      videoEventBus?.current?.on(listener.eventName, listener.callback);
    });
  };

  const setVideoEventBus = (bus) => {
    videoEventBus.current = bus;
    mountOutstandingListeners();
  };

  const sendVideoPlayerEvent = (eventName, payload = {}) => {
    videoEventBus?.current?.emit(eventName, payload);
  };

  const addVideoPlayerEventListener = (eventName, callback) => {
    const canMountEvent = !!videoEventBus.current;

    if (!canMountEvent) {
      // If the event bus is not available, save reference for them until it is.
      unmountedEvents.current?.push({ eventName, callback });
      return;
    }

    videoEventBus?.current?.on(eventName, callback);
  };

  const removeVideoPlayerEventListener = (eventName, callback) => {
    videoEventBus?.current?.off(eventName, callback);
  };

  return (
    <VideoEventBusContext.Provider
      value={{
        setVideoEventBus,
        sendVideoPlayerEvent,
        addVideoPlayerEventListener,
        removeVideoPlayerEventListener,
      }}
    >
      {children}
    </VideoEventBusContext.Provider>
  );
};

const useVideoEventBus = () => {
  const eventBusContext = useContext(VideoEventBusContext);

  if (eventBusContext === undefined) {
    throw new Error(
      "useEventBus must be used within a VideoEventBusContextProvider.",
    );
  }

  return eventBusContext;
};

VideoEventBusContextProvider.propTypes = propTypes;

export default useVideoEventBus;
